import { useState } from "react";
import { useTranslation } from "react-i18next";
import { useToast } from "../../../hooks/useToast";
import { useLocale } from "../../../hooks/useLocale";
import { usePrivacyPreferences } from "../../../hooks/usePrivacyPreferences";
import {
  useCvViewingPrivacy,
  useUpdateCvViewingPrivacy,
} from "../../../hooks/useAnalyticsApi";
import { SubjectReviewPanel } from "../SubjectReviewPanel";
import { CompanyLiveProfileAccessPanel } from "../CompanyLiveProfileAccessPanel";
import { CvPrivacyControls } from "./CvPrivacyControls";
import {
  openAccountGuide,
  openMarketingLink,
} from "../../../lib/marketing-links";
import {
  accountExportFileName,
  fetchAccountDataExport,
} from "../../../lib/account-export-api";
import { saveAccountExport } from "../../../lib/account-export-save";
import {
  ControlStack,
  ControlHeading,
  ControlSection,
  ControlRow,
  ControlHelp,
  ControlToggle,
} from "./DataControlParts";

export function PrivacyPanel() {
  const { t } = useTranslation("common");
  const { locale } = useLocale();
  const {
    isHydrated,
    analyticsEnabled,
    marketingEnabled,
    setAnalyticsConsent,
    setMarketingConsent,
  } = usePrivacyPreferences();
  const toast = useToast();
  const viewingPrivacy = useCvViewingPrivacy();
  const updateViewingPrivacy = useUpdateCvViewingPrivacy();
  const [savingAnalytics, setSavingAnalytics] = useState(false);
  const [savingMarketing, setSavingMarketing] = useState(false);
  // "Download my data" (E10). The state is shown in the row itself: a toast
  // alone is invisible over the iOS settings form sheet.
  const [exportingData, setExportingData] = useState(false);
  const [exportResult, setExportResult] = useState<"ready" | "failed" | null>(
    null,
  );
  const updateAnalytics = async (enabled: boolean) => {
    if (!isHydrated || savingAnalytics) return;
    setSavingAnalytics(true);
    try {
      await setAnalyticsConsent(enabled ? "granted" : "denied", "settings");
      toast.success(t("settings.privacy.saved"));
    } catch {
      toast.error(t("settings.privacy.updateFailed"));
    } finally {
      setSavingAnalytics(false);
    }
  };
  // Off by default: a signed-in member who opens someone's CV shows up by
  // name in that owner's analytics unless they choose to browse privately.
  const updateViewCvsPrivately = async (enabled: boolean) => {
    if (!viewingPrivacy.data || updateViewingPrivacy.isPending) return;
    try {
      await updateViewingPrivacy.mutateAsync({ viewCvsPrivately: enabled });
      toast.success(t("settings.privacy.saved"));
    } catch {
      toast.error(t("settings.privacy.updateFailed"));
    }
  };
  const updateMarketing = async (enabled: boolean) => {
    if (!isHydrated || savingMarketing) return;
    setSavingMarketing(true);
    try {
      await setMarketingConsent(enabled ? "granted" : "denied", "settings");
      toast.success(t("settings.privacy.saved"));
    } catch {
      toast.error(t("settings.privacy.updateFailed"));
    } finally {
      setSavingMarketing(false);
    }
  };
  const downloadData = async () => {
    if (exportingData) return;
    setExportingData(true);
    setExportResult(null);
    try {
      const data = await fetchAccountDataExport();
      await saveAccountExport(
        JSON.stringify(data, null, 2),
        accountExportFileName(),
        t("settings.privacy.downloadData"),
      );
      setExportResult("ready");
      toast.success(t("settings.privacy.downloadDataReady"));
    } catch {
      setExportResult("failed");
      toast.error(t("settings.privacy.downloadDataFailed"));
    } finally {
      setExportingData(false);
    }
  };
  const downloadDataStatus = exportingData
    ? t("settings.privacy.downloadDataPreparing")
    : exportResult === "failed"
      ? t("settings.privacy.downloadDataFailed")
      : exportResult === "ready"
        ? t("settings.privacy.downloadDataReady")
        : t("settings.privacy.downloadDataDescription");
  return (
    <ControlStack gap={24}>
      <ControlHeading
        title={t("settings.privacy.title")}
        description={t("settings.cvPrivacy.description")}
      />
      <CvPrivacyControls />
      <ControlSection title={t("settings.employerAccess.title")}>
        <CompanyLiveProfileAccessPanel />
      </ControlSection>
      <ControlSection title={t("settings.subjectReview.title")}>
        <SubjectReviewPanel onRerunExport={downloadData} />
      </ControlSection>
      <ControlSection title={t("settings.controls.appPrivacy")}>
        <ControlToggle
          title={t("settings.privacy.viewCvsPrivately")}
          description={t("settings.privacy.viewCvsPrivatelyDescription")}
          checked={viewingPrivacy.data?.viewCvsPrivately ?? false}
          disabled={!viewingPrivacy.data || updateViewingPrivacy.isPending}
          onChange={(enabled) => void updateViewCvsPrivately(enabled)}
        />
        <ControlToggle
          title={t("settings.privacy.productAnalytics")}
          description={t("settings.privacy.productAnalyticsDescription")}
          checked={analyticsEnabled}
          disabled={!isHydrated || savingAnalytics}
          onChange={(enabled) => void updateAnalytics(enabled)}
        />
        <ControlToggle
          title={t("settings.privacy.marketing")}
          description={t("settings.privacy.marketingDescription")}
          checked={marketingEnabled}
          disabled={!isHydrated || savingMarketing}
          onChange={(enabled) => void updateMarketing(enabled)}
        />
        <ControlRow
          title={t("settings.privacyPolicy")}
          icon="privacy"
          onPress={() => void openMarketingLink("privacy", locale)}
        />
      </ControlSection>
      <ControlSection title={t("settings.privacy.yourData")}>
        <ControlRow
          title={t("settings.privacy.downloadData")}
          description={downloadDataStatus}
          icon="download"
          disabled={exportingData}
          onPress={() => void downloadData()}
        />
      </ControlSection>
      <ControlHelp
        title={t("settings.cvPrivacy.guide")}
        onPress={() => void openAccountGuide("privacy", locale)}
      />
    </ControlStack>
  );
}
